import { useState, useEffect, FC } from 'react';

import { Formik, type FormikHelpers } from 'formik';
import { useTasksContext } from 'modules/account/sales/containers/Tasks';
import { useGetTask, useUpdateTask } from 'modules/account/sales/hooks';
import { TASK_FORM, TASK_SCHEMA } from 'modules/account/sales/forms/schema';
import EditTaskForm from 'modules/account/sales/forms/EditTaskForm/EditTaskForm';
import { useUploadToUploadCare } from 'utils/helpers';

import useModal from '../Modal/useModal';

interface PROPS {
  mode: 'popover' | 'modal';
}

const ComposerTaskBody: FC<PROPS> = ({ mode = 'popover' }) => {
  const { hideModal } = useModal();
  const { selectedTaskId, clearTaskId } = useTasksContext();
  const { data: task } = useGetTask(selectedTaskId as string);
  const { mutate: updateTask } = useUpdateTask();
  const { mutateAsync: uploadFiles } = useUploadToUploadCare();

  const [attachmentsToUpload, setAttachmentsToUpload] = useState<File[]>([]);

  useEffect(() => {
    setAttachmentsToUpload([]);
  }, [selectedTaskId]);

  if (!task) {
    return <div />;
  }

  const FORM_INIT = {
    title: task.title ?? '',
    type: task.type ?? '',
    dueAt: task.dueAt ?? new Date(),
    content: task.content ?? '',
    leadId: task.leadId ?? null,
    status: task.status ?? 'PENDING',
    priority: task.priority ?? 'LOW',
    accountUserId: task.accountUserId ?? [],
    attachments: task.attachments,
    tagId: task.tagId,
    dealId: task.dealId,
  } as TASK_FORM;

  const onSubmit = async (
    { ...rest }: TASK_FORM,
    { setSubmitting }: FormikHelpers<TASK_FORM>,
  ) => {
    const uploadedAttachments = attachmentsToUpload.length
      ? await uploadFiles(attachmentsToUpload)
      : { files: [] };

    const tagId = TASK_SCHEMA.cast(rest).tagId?.map((tag) =>
      typeof tag === 'string' ? tag : tag?._id,
    );

    updateTask(
      {
        id: selectedTaskId as string,
        values: {
          ...rest,
          tagId,
          attachments: [
            ...uploadedAttachments.files.map((file) => ({
              fileUrl: file.cdnUrl,
              fileType: file.mimeType,
              fileName: file.originalFilename,
              fileSize: file.size,
              source: 'Task',
              ...(file.isImage && { fileThumbnail: file.cdnUrl }),
            })),
          ],
        },
      },
      {
        onSuccess: () => {
          hideModal();
          setAttachmentsToUpload([]);
          clearTaskId?.();
        },
        onSettled: () => setSubmitting(false),
      },
    );
  };

  return (
    <Formik
      initialValues={{ ...FORM_INIT }}
      onSubmit={onSubmit}
      validationSchema={TASK_SCHEMA}
    >
      {() => (
        <EditTaskForm
          mode={mode}
          attachmentsToUpload={attachmentsToUpload}
          setAttachmentsToUpload={setAttachmentsToUpload}
        />
      )}
    </Formik>
  );
};

export default ComposerTaskBody;
